// ./src/components/features/authentication/SigninForm/SigninForm.tsx
import React, { useState } from "react";
import {
  Stack,
  Button,
  TextInput,
  PasswordInput,
  Text,
  Anchor,
  Alert,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle, IconCheck } from "@tabler/icons-react";

import { useApp } from "@/contexts/AppContext";

import { User } from "@/utils";

export interface SigninFormData {
  username: string;
  password: string;
}

export interface SigninFormErrors {
  username?: string;
  password?: string;
  general?: string;
}

export interface SigninFormProps {
  /** Callback function called when user successfully signs in */
  onSigninSuccess?: (userData: User) => void;

  /** Callback function called when user wants to navigate to signup */
  onNavigateToSignup?: () => void;

  /** Callback function called when user wants to navigate to forgot password */
  onNavigateToForgotPassword?: () => void;

  /** Whether to show the "Don't have an account?" link */
  showSignupLink?: boolean;
}

export const SigninForm: React.FC<SigninFormProps> = ({
  onSigninSuccess,
  onNavigateToSignup,
  onNavigateToForgotPassword,
  showSignupLink = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<SigninFormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { signInWithCredentials } = useApp();

  const form = useForm<SigninFormData>({
    initialValues: {
      username: "",
      password: "",
    },
    validate: {
      username: (value) => {
        if (!value) return "Username is required";
        return null;
      },
      password: (value) => {
        if (!value) return "Password is required";
        return null;
      },
    },
  });

  const handleSubmit = async (values: SigninFormData) => {
    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      // Call the real AuthService API
      const result = await signInWithCredentials(
        values.username,
        values.password
      );

      if (result.didSucceed()) {
        setSuccessMessage("Sign in successful! Redirecting...");

        // Notify parent component that signin was successful
        setTimeout(() => {
          onSigninSuccess?.(result.getDataFirst());
        }, 1000);
      } else {
        throw Error(result.getErrorSummary());
      }
    } catch (error) {
      console.error("Signin error:", error);
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "An error occurred while signing in. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignupLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigateToSignup?.();
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onNavigateToForgotPassword?.();
  };

  // Show success state
  if (successMessage) {
    return (
      <Alert color="green" icon={<IconCheck size={16} />}>
        {successMessage}
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Username"
          placeholder="Enter your username"
          required
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
        />

        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          required
          {...form.getInputProps("password")}
          error={form.errors.password || serverErrors.password}
        />

        <Box ta="right" mt={-8}>
          <Anchor
            onClick={handleForgotPasswordClick}
            size="sm"
            style={{ cursor: "pointer" }}
          >
            Forgot Password?
          </Anchor>
        </Box>

        {serverErrors.general && (
          <Alert color="red" icon={<IconInfoCircle size={16} />}>
            {serverErrors.general}
          </Alert>
        )}

        <Button type="submit" fullWidth size="md" mt="md" loading={loading}>
          Sign In
        </Button>

        {showSignupLink && (
          <Text ta="center" size="sm" c="dimmed">
            Don't have an account?{" "}
            <Anchor
              onClick={handleSignupLinkClick}
              fw={500}
              style={{ cursor: "pointer" }}
            >
              Create one here
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
};
